// Editing a callout: pressing the colored bar on its left opens a menu of kinds
// (= colors) ("let me change this color").
//
// The data model is untouched: the node and its markdown stay in the editor
// package; only node views are added. The kinds are GitHub's five alerts and
// each has its color (no free colors: the markdown `> [!NOTE]` can carry only
// a kind).
//
// A React node view (ReactNodeViewRenderer + NodeViewContent): the title and
// body are the contentDOM, edited by ProseMirror as usual. The static view
// draws renderHTML's DOM without the node view, so the CSS is written with
// descendant selectors to fit both.
import type { NodeViewProps } from "@tiptap/core";
import {
  NodeViewContent,
  NodeViewWrapper,
  ReactNodeViewRenderer,
} from "@tiptap/react";
import {
  blockIdDomAttrs,
  CALLOUT_KINDS,
  Callout,
  type CalloutKind,
  CalloutTitle,
} from "@trameria/editor";
import { m } from "@trameria/i18n";
import { useState } from "react";
import { FloatingMenu } from "./floating-menu";
import { ignoreMutationForReactNodeView } from "./react-node-view-mutation";

/**
 * The menu names colors (blue / green / …), not meanings (info / warning):
 * "do not decide names for me" - what it means is the writer's title. The
 * markdown `[!NOTE]` stays as the way to carry the color.
 */
export const CALLOUT_KIND_LABELS: Record<CalloutKind, () => string> = {
  note: () => m.blocks_callout_blue(),
  tip: () => m.blocks_callout_green(),
  important: () => m.blocks_callout_purple(),
  warning: () => m.blocks_callout_yellow(),
  caution: () => m.blocks_callout_red(),
};

/** The menu's swatches: the same variables the callout's bar uses (blocks.css). */
export const CALLOUT_KIND_COLORS: Record<CalloutKind, string> = {
  note: "var(--trameria-callout-note)",
  tip: "var(--trameria-callout-tip)",
  important: "var(--trameria-callout-important)",
  warning: "var(--trameria-callout-warning)",
  caution: "var(--trameria-callout-caution)",
};

function isCalloutKind(value: unknown): value is CalloutKind {
  return (CALLOUT_KINDS as readonly string[]).includes(String(value));
}

function CalloutView({ node, editor, updateAttributes }: NodeViewProps) {
  const kind: CalloutKind = isCalloutKind(node.attrs.kind)
    ? node.attrs.kind
    : "note";
  const [menu, setMenu] = useState<DOMRect | null>(null);
  /** The focus goes back to the body before closing; the selection is still there. */
  const close = () => {
    setMenu(null);
    editor.commands.focus();
  };

  return (
    // renderHTML's attributes on the wrapper (data-type / data-kind /
    // data-block-id): the CSS and block hit areas look at this div.
    <NodeViewWrapper
      as="div"
      data-type="callout"
      data-kind={kind}
      {...blockIdDomAttrs(node)}
    >
      {/* The bar on the left. A BUTTON, so TipTap's stopEvent keeps its
          mousedown from PM; preventDefault keeps the selection and focus. Not
          in the Tab order. */}
      <button
        type="button"
        contentEditable={false}
        aria-label={m.blocks_callout_change_color({
          color: CALLOUT_KIND_LABELS[kind](),
        })}
        aria-haspopup="menu"
        aria-expanded={menu !== null}
        tabIndex={-1}
        className="trameria-callout-kind"
        onMouseDown={(e) => e.preventDefault()}
        onClick={(e) => setMenu(e.currentTarget.getBoundingClientRect())}
      />
      <NodeViewContent as="div" />
      {menu && (
        <FloatingMenu
          anchor={menu}
          placement="right"
          label={m.blocks_callout_color()}
          items={CALLOUT_KINDS.map((k) => ({
            label: CALLOUT_KIND_LABELS[k](),
            swatch: CALLOUT_KIND_COLORS[k],
            selected: k === kind,
            onSelect: () => {
              // `updateAttributes` merges into the attributes, so the block id stays.
              if (k !== kind) updateAttributes({ kind: k });
              close();
            },
          }))}
          onClose={close}
        />
      )}
    </NodeViewWrapper>
  );
}

/**
 * The title carries its placeholder (`data-placeholder`); the CSS shows it with
 * `content: attr(data-placeholder)` (the text is not in the CSS, so it follows
 * the screen's language). The DOM is renderHTML's plus the placeholder;
 * ProseMirror edits the content as usual.
 */
export const CalloutTitleWithPlaceholder = CalloutTitle.extend({
  addNodeView() {
    return ({ HTMLAttributes }) => {
      const dom = document.createElement("div");
      for (const [k, v] of Object.entries(HTMLAttributes))
        if (v !== null && v !== undefined) dom.setAttribute(k, String(v));
      dom.setAttribute("data-type", "calloutTitle");
      dom.setAttribute(
        "data-placeholder",
        m.blocks_callout_title_placeholder(),
      );
      return { dom, contentDOM: dom };
    };
  },
});

/** The callout with the color menu. The node and its markdown are unchanged. */
export const CalloutWithKindMenu = Callout.extend({
  addNodeView() {
    return ReactNodeViewRenderer(CalloutView, {
      // Keeps iPhones from freezing when one is inserted (react-node-view-mutation.ts).
      ignoreMutation: ignoreMutationForReactNodeView(this.editor),
    });
  },
});
